/**
 * DateChangeRebookingModal
 * Modal for handling date changes on assignments that are "planerad" or later.
 * Allows admin to choose what happens to customer status and each TG's status.
 */

import { useState, useEffect } from 'react'
import { X, Calendar, Loader2, User, Leaf, MessageSquare, RotateCcw, ChevronDown, ChevronRight, Clock, Info } from 'lucide-react'
import type { CustomerCommStatus, TgWorkStatus } from '../../types/statusTypes'

type CustomerStatusAction = 'keep' | 'reset_to_ej_kontaktad' | 'set_informerad'
type TgStatusAction = 'keep' | 'remove'

interface TgInfo {
  id: string
  subcontractorId: string
  name: string
  phone: string | null
  currentStatus: TgWorkStatus
}

interface TgActionChoice {
  action: TgStatusAction
  removeReason?: string
  extraMessage?: string  // Extra info for 'keep' action
}

export interface DateChangeResult {
  customerAction: CustomerStatusAction
  resetToPreliminar: boolean
  preliminarMessage?: string  // Extra info for reset to preliminar
  revertToPlanned: boolean  // When pågående assignment is moved to future date
  tgActions: Record<string, TgActionChoice>  // keyed by assignment_subcontractor id
  sendSmsTo: Record<string, boolean>  // keyed by assignment_subcontractor id
}

interface DateChangeRebookingModalProps {
  isOpen: boolean
  onClose: () => void
  originalDate: string
  newDate: string
  startTime?: string | null
  // Time change props
  originalStartTime?: string | null
  originalEndTime?: string | null
  newStartTime?: string | null
  newEndTime?: string | null
  isTimeChangeOnly?: boolean  // True if only time changed, not date
  assignmentNumber?: string
  assignmentTitle: string
  customerName: string
  customerCurrentStatus: CustomerCommStatus
  currentAssignmentStatus?: string  // To detect pågående → future date scenario
  tgList: TgInfo[]
  onConfirm: (result: DateChangeResult) => Promise<void>
}

const CUSTOMER_STATUS_OPTIONS: { value: CustomerStatusAction; label: string; description: string }[] = [
  { value: 'keep', label: 'Behåll nuvarande status', description: 'Kunden har redan godkänt ändringen' },
  { value: 'reset_to_ej_kontaktad', label: 'Återställ till Ej kontaktad', description: 'Behöver informera om nytt datum' },
  { value: 'set_informerad', label: 'Sätt som Informerad', description: 'Har redan meddelat kunden' }
]

export function DateChangeRebookingModal({
  isOpen,
  onClose,
  originalDate,
  newDate,
  startTime,
  originalStartTime,
  originalEndTime,
  newStartTime,
  newEndTime,
  isTimeChangeOnly = false,
  assignmentNumber,
  assignmentTitle,
  customerName,
  customerCurrentStatus,
  currentAssignmentStatus,
  tgList,
  onConfirm
}: DateChangeRebookingModalProps) {
  const [customerAction, setCustomerAction] = useState<CustomerStatusAction>('reset_to_ej_kontaktad')
  const [resetToPreliminar, setResetToPreliminar] = useState(false)
  const [preliminarMessage, setPreliminarMessage] = useState('')  // Extra info when resetting to preliminar

  // Check if we're moving a pågående assignment to a future date
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const newDateObj = new Date(newDate)
  newDateObj.setHours(0, 0, 0, 0)
  const isMovingToFuture = newDateObj > today
  const willRevertToPlanned = currentAssignmentStatus === 'pagaende' && isMovingToFuture && !resetToPreliminar
  const [tgActions, setTgActions] = useState<Record<string, TgActionChoice>>(() => {
    const initial: Record<string, TgActionChoice> = {}
    tgList.forEach(tg => {
      initial[tg.id] = { action: 'keep' }
    })
    return initial
  })
  const [customReasons, setCustomReasons] = useState<Record<string, string>>({})
  const [customMessages, setCustomMessages] = useState<Record<string, string>>({})  // Extra info for kept TGs
  const [sendSmsTo, setSendSmsTo] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    tgList.forEach(tg => {
      // Default to true if TG has phone number
      initial[tg.id] = !!tg.phone
    })
    return initial
  })
  const [expandedSms, setExpandedSms] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update customReasons in tgActions when reason changes
  useEffect(() => {
    // Sync customReasons to tgActions
  }, [customReasons])

  const handleTgActionChange = (tgId: string, action: TgStatusAction) => {
    setTgActions(prev => ({
      ...prev,
      [tgId]: { ...prev[tgId], action }
    }))
  }

  const handleReasonChange = (tgId: string, reason: string) => {
    setCustomReasons(prev => ({ ...prev, [tgId]: reason }))
  }

  const handleMessageChange = (tgId: string, message: string) => {
    setCustomMessages(prev => ({ ...prev, [tgId]: message }))
  }

  const toggleSendSms = (tgId: string) => {
    setSendSmsTo(prev => ({ ...prev, [tgId]: !prev[tgId] }))
  }

  const toggleSmsExpanded = (tgId: string) => {
    setExpandedSms(prev => ({ ...prev, [tgId]: !prev[tgId] }))
  }

  const handleConfirm = async () => {
    // If not resetting to preliminar, validate remove reasons
    if (!resetToPreliminar) {
      for (const tg of tgList) {
        const action = tgActions[tg.id]
        if (action.action === 'remove' && !customReasons[tg.id]?.trim()) {
          alert(`Ange anledning för att ta bort ${tg.name}`)
          return
        }
      }
    }

    // Build final result with reasons and extra messages
    const finalTgActions: Record<string, TgActionChoice> = {}
    for (const tg of tgList) {
      const action = tgActions[tg.id]
      if (action.action === 'remove') {
        finalTgActions[tg.id] = {
          action: 'remove',
          removeReason: customReasons[tg.id]?.trim()
        }
      } else if (action.action === 'keep') {
        finalTgActions[tg.id] = {
          action: 'keep',
          extraMessage: customMessages[tg.id]?.trim()
        }
      } else {
        finalTgActions[tg.id] = action
      }
    }

    setIsSubmitting(true)
    try {
      await onConfirm({
        customerAction,
        resetToPreliminar,
        preliminarMessage: preliminarMessage.trim() || undefined,
        revertToPlanned: willRevertToPlanned,
        tgActions: finalTgActions,
        sendSmsTo
      })
      onClose()
    } catch (error) {
      console.error('Error confirming date change:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  const getCustomerStatusLabel = (status: CustomerCommStatus) => {
    switch (status) {
      case 'ej_kontaktad': return 'Ej kontaktad'
      case 'informerad': return 'Informerad'
      case 'bekraftad': return 'Bekräftad'
      case 'avbokad': return 'Avbokad'
      default: return status
    }
  }

  const getTgStatusLabel = (status: TgWorkStatus) => {
    switch (status) {
      case 'preliminar_tilldelad': return 'Preliminärt tilldelad'
      case 'tilldelad': return 'Tilldelad'
      case 'ej_rapporterad': return 'Ej rapporterad'
      case 'rapporterad': return 'Rapporterad'
      case 'komplettering': return 'Komplettering'
      case 'godkand': return 'Godkänd'
      case 'avsagt': return 'Avsagt'
      default: return status
    }
  }

  // Format time to HH:MM (remove seconds if present)
  const formatTime = (time: string | null | undefined) => {
    if (!time) return ''
    // Handle HH:MM:SS format -> HH:MM
    return time.substring(0, 5)
  }

  // Format assignment identifier (# + number + title)
  const getAssignmentIdentifier = () => {
    if (assignmentNumber) {
      return `#${assignmentNumber} ${assignmentTitle}`
    }
    return assignmentTitle
  }

  // Format date with time (HH:MM)
  const formatDateTime = (date: string, time?: string | null) => {
    const formattedTime = formatTime(time)
    if (formattedTime) {
      return `${date} ${formattedTime}`
    }
    return date
  }

  // Get SMS message based on action
  const getSmsMessage = (tgId: string, action: TgStatusAction) => {
    const identifier = getAssignmentIdentifier()

    // Format original and new date/time based on change type
    let originalDateTime: string
    let newDateTime: string

    if (isTimeChangeOnly) {
      // Time-only change: show date + old time → date + new time
      const timeFrom = `${formatTime(originalStartTime)} - ${formatTime(originalEndTime)}`
      const timeTo = `${formatTime(newStartTime)} - ${formatTime(newEndTime)}`
      originalDateTime = `${originalDate} kl ${timeFrom}`
      newDateTime = `${originalDate} kl ${timeTo}`
    } else {
      // Date change (with or without time)
      originalDateTime = formatDateTime(originalDate, startTime)
      newDateTime = formatDateTime(newDate, startTime)
    }

    if (resetToPreliminar) {
      const baseMessage = `Uppdraget ${identifier} (planerat ${originalDateTime}) behöver planeras om.`
      const extraMessage = preliminarMessage.trim()
      if (extraMessage) {
        return `${baseMessage} ${extraMessage}`
      }
      return `${baseMessage} Vi återkopplar när vi vet mera.`
    }

    if (action === 'keep') {
      const extraMessage = customMessages[tgId]?.trim()
      const changeDescription = isTimeChangeOnly
        ? `Uppdraget ${identifier} har ändrat tid från ${originalDateTime} till ${newDateTime}.`
        : `Uppdraget ${identifier} har flyttats från ${originalDateTime} till ${newDateTime}.`
      if (extraMessage) {
        return `${changeDescription} ${extraMessage}`
      }
      return changeDescription
    }

    if (action === 'remove') {
      const reason = customReasons[tgId]?.trim()
      if (reason) {
        return `Du har tagits bort från uppdraget ${identifier} (planerat ${originalDateTime}). ${reason}`
      }
      return `Du har tagits bort från uppdraget ${identifier} (planerat ${originalDateTime}).`
    }

    return null
  }

  // Count total SMS to be sent
  const getTotalSmsCount = () => {
    return tgList.filter(tg => {
      if (!tg.phone) return false
      if (!sendSmsTo[tg.id]) return false
      return true // SMS can be sent for both keep and remove actions
    }).length
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                <Calendar className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  {isTimeChangeOnly ? 'Tid ändras' : 'Datum ändras'}
                </h3>
                <p className="text-sm text-nature-500 dark:text-nature-400">
                  Välj hur statusar ska hanteras
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="px-6 py-4 space-y-6 overflow-y-auto flex-1">
          {/* Date/Time change info */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-center justify-center gap-6">
              <div className="text-center">
                <p className="text-xs text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-2">Från</p>
                <div className="flex flex-col items-center gap-1 text-amber-700 dark:text-amber-300 line-through opacity-75">
                  {/* Show date if it changed or if not time-only */}
                  {!isTimeChangeOnly && (
                    <span className="flex items-center gap-1 text-sm">
                      <Calendar className="h-3.5 w-3.5" />
                      {originalDate}
                    </span>
                  )}
                  {/* Show time - either original time for time changes, or startTime for date changes */}
                  {(isTimeChangeOnly || originalStartTime || startTime) && (
                    <span className="flex items-center gap-1 text-sm">
                      <Clock className="h-3.5 w-3.5" />
                      {isTimeChangeOnly
                        ? `${formatTime(originalStartTime)} - ${formatTime(originalEndTime)}`
                        : formatTime(startTime)
                      }
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center text-amber-500 dark:text-amber-400">
                <span className="text-2xl">→</span>
              </div>
              <div className="text-center">
                <p className="text-xs text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-2">Till</p>
                <div className="flex flex-col items-center gap-1 text-amber-800 dark:text-amber-200 font-semibold">
                  {/* Show date if it changed or if not time-only */}
                  {!isTimeChangeOnly && (
                    <span className="flex items-center gap-1 text-sm">
                      <Calendar className="h-3.5 w-3.5" />
                      {newDate}
                    </span>
                  )}
                  {/* Show time - either new time for time changes, or startTime for date changes */}
                  {(isTimeChangeOnly || newStartTime || startTime) && (
                    <span className="flex items-center gap-1 text-sm">
                      <Clock className="h-3.5 w-3.5" />
                      {isTimeChangeOnly
                        ? `${formatTime(newStartTime)} - ${formatTime(newEndTime)}`
                        : formatTime(startTime)
                      }
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Info about reverting to planerad when moving pågående to future */}
          {willRevertToPlanned && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium">Uppdraget återgår till Planerad</p>
                  <p className="text-blue-600 dark:text-blue-300 mt-1">
                    Eftersom uppdraget är pågående och flyttas till ett framtida datum, ändras status till "Planerad"
                    och TG:s som är "Ej rapporterad" återställs till "Tilldelad".
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Reset to preliminar option */}
          {tgList.length > 0 && (
            <div className="border border-nature-200 dark:border-nature-700 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetToPreliminar}
                  onChange={(e) => setResetToPreliminar(e.target.checked)}
                  className="mt-1 h-4 w-4 text-primary-600 rounded"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <RotateCcw className="h-4 w-4 text-amber-600" />
                    <span className="font-medium text-nature-800 dark:text-nature-200">
                      Återställ till preliminär planering
                    </span>
                  </div>
                  <p className="text-sm text-nature-500 dark:text-nature-400 mt-1">
                    Uppdraget blir preliminärt igen och alla TG:s återställs till preliminärt tilldelade.
                  </p>
                </div>
              </label>

              {/* Extra message input for preliminar reset */}
              {resetToPreliminar && (
                <div className="mt-3 ml-7">
                  <input
                    type="text"
                    value={preliminarMessage}
                    onChange={(e) => setPreliminarMessage(e.target.value)}
                    placeholder="Extra info i SMS (valfritt)..."
                    className="input-field text-sm w-full"
                  />
                </div>
              )}
            </div>
          )}

          {/* Customer status section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-nature-700 dark:text-nature-300">
              <User className="h-4 w-4" />
              <span className="font-medium">Kundstatus</span>
              <span className="text-sm text-nature-500">
                ({customerName} - nuvarande: {getCustomerStatusLabel(customerCurrentStatus)})
              </span>
            </div>

            <div className="space-y-2 pl-6">
              {CUSTOMER_STATUS_OPTIONS.map(option => (
                <label
                  key={option.value}
                  className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                    customerAction === option.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-nature-200 dark:border-nature-600 hover:border-nature-300 dark:hover:border-nature-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="customerAction"
                    value={option.value}
                    checked={customerAction === option.value}
                    onChange={() => setCustomerAction(option.value)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <p className={`text-sm font-medium ${
                      customerAction === option.value
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-nature-700 dark:text-nature-300'
                    }`}>
                      {option.label}
                    </p>
                    <p className="text-xs text-nature-500 dark:text-nature-400">
                      {option.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* TG status section */}
          {tgList.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-nature-700 dark:text-nature-300">
                <Leaf className="h-4 w-4" />
                <span className="font-medium">Trädgårdsmästare</span>
              </div>

              <div className="space-y-4 pl-6">
                {tgList.map(tg => {
                  const action = tgActions[tg.id]?.action || 'keep'
                  const smsMessage = getSmsMessage(tg.id, action)
                  const canSendSms = tg.phone && smsMessage
                  const willSendSms = canSendSms && sendSmsTo[tg.id]
                  const isExpanded = expandedSms[tg.id]

                  return (
                    <div key={tg.id} className="border border-nature-200 dark:border-nature-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-nature-800 dark:text-nature-200">{tg.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-400">
                            {getTgStatusLabel(tg.currentStatus)}
                          </span>
                        </div>
                        {/* SMS indicator */}
                        {canSendSms && (
                          <button
                            type="button"
                            onClick={() => toggleSmsExpanded(tg.id)}
                            className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                              willSendSms
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'bg-nature-100 text-nature-500 dark:bg-nature-700 dark:text-nature-400'
                            }`}
                          >
                            <MessageSquare className="h-3 w-3" />
                            <span>{willSendSms ? 'SMS' : 'Inget SMS'}</span>
                            {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </button>
                        )}
                        {!tg.phone && (
                          <span className="text-xs text-nature-400 dark:text-nature-500 italic">
                            Inget telefonnummer
                          </span>
                        )}
                      </div>

                      {/* SMS Preview - collapsible */}
                      {canSendSms && isExpanded && (
                        <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                              SMS till {tg.phone}
                            </span>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={sendSmsTo[tg.id] || false}
                                onChange={() => toggleSendSms(tg.id)}
                                className="h-3.5 w-3.5 text-primary-600 rounded"
                              />
                              <span className="text-xs text-blue-600 dark:text-blue-400">Skicka</span>
                            </label>
                          </div>
                          <div className={`bg-white dark:bg-nature-800 rounded p-2 border border-blue-200 dark:border-blue-700 ${!sendSmsTo[tg.id] ? 'opacity-50' : ''}`}>
                            <p className="text-xs text-nature-700 dark:text-nature-300 font-mono">
                              {smsMessage}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Action options - only show if NOT resetting to preliminar */}
                      {!resetToPreliminar && (
                        <div className="space-y-2">
                          {/* Keep option */}
                          <div>
                            <label
                              className={`flex items-start p-2 rounded border cursor-pointer transition-colors ${
                                action === 'keep'
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                  : 'border-nature-200 dark:border-nature-600 hover:border-nature-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`tgAction-${tg.id}`}
                                value="keep"
                                checked={action === 'keep'}
                                onChange={() => handleTgActionChange(tg.id, 'keep')}
                                className="mt-0.5 mr-2"
                              />
                              <div>
                                <p className={`text-sm ${
                                  action === 'keep'
                                    ? 'text-primary-700 dark:text-primary-300 font-medium'
                                    : 'text-nature-700 dark:text-nature-300'
                                }`}>
                                  Behåll tilldelning
                                </p>
                                <p className="text-xs text-nature-500">Trädgårdsmästare kvarstår på uppdraget</p>
                              </div>
                            </label>

                            {/* Extra message input for keep */}
                            {action === 'keep' && (
                              <div className="mt-2 ml-6">
                                <input
                                  type="text"
                                  value={customMessages[tg.id] || ''}
                                  onChange={(e) => handleMessageChange(tg.id, e.target.value)}
                                  placeholder="Extra info i SMS (valfritt)..."
                                  className="input-field text-sm w-full"
                                />
                              </div>
                            )}
                          </div>

                          {/* Remove option */}
                          <div>
                            <label
                              className={`flex items-start p-2 rounded border cursor-pointer transition-colors ${
                                action === 'remove'
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                  : 'border-nature-200 dark:border-nature-600 hover:border-nature-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`tgAction-${tg.id}`}
                                value="remove"
                                checked={action === 'remove'}
                                onChange={() => handleTgActionChange(tg.id, 'remove')}
                                className="mt-0.5 mr-2"
                              />
                              <div>
                                <p className={`text-sm ${
                                  action === 'remove'
                                    ? 'text-primary-700 dark:text-primary-300 font-medium'
                                    : 'text-nature-700 dark:text-nature-300'
                                }`}>
                                  Ta bort från uppdraget
                                </p>
                                <p className="text-xs text-nature-500">Trädgårdsmästare tas bort (ange anledning)</p>
                              </div>
                            </label>

                            {/* Remove reason input */}
                            {action === 'remove' && (
                              <div className="mt-2 ml-6">
                                <input
                                  type="text"
                                  value={customReasons[tg.id] || ''}
                                  onChange={(e) => handleReasonChange(tg.id, e.target.value)}
                                  placeholder="Ange anledning..."
                                  className="input-field text-sm w-full"
                                  autoFocus
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Show message when resetting to preliminar */}
                      {resetToPreliminar && (
                        <p className="text-sm text-nature-500 dark:text-nature-400 italic">
                          Återställs till preliminärt tilldelad
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900 flex-shrink-0">
          <div className="flex items-center justify-between">
            {/* SMS summary */}
            <div className="text-sm text-nature-500 dark:text-nature-400">
              {getTotalSmsCount() > 0 ? (
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  {getTotalSmsCount()} SMS skickas
                </span>
              ) : (
                <span className="italic">Inga SMS skickas</span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="btn-outline px-4 py-2"
                disabled={isSubmitting}
              >
                Avbryt
              </button>
              <button
                onClick={handleConfirm}
                disabled={isSubmitting}
                className="btn-primary px-4 py-2 flex items-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Sparar...</span>
                  </>
                ) : (
                  <span>{isTimeChangeOnly ? 'Spara tidändring' : 'Spara datumändring'}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
